import React from 'react'
import cn from 'classnames'
import { useQuery } from '@tanstack/react-query'
import { Sprout } from 'lucide-react'
import api from 'api/api'
import styles from 'components/modals/wallet.module.scss'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import useUserHasFeature from 'refactor/hooks/useUserHasFeature'

// Wallet "Seeds" section: every post the user has seeded, their position,
// what they put in, and what later seeders have paid them back.
export default function WalletSeeds() {
    const seedingEnabled = useUserHasFeature('seeding')
    const { formatSatsAmount } = useBsvDisplay()
    const currentUserId = gon.currentUser?.id

    const { data } = useQuery({
        queryKey: ['wallet-seeds', currentUserId],
        queryFn: api.getWalletSeeds,
        enabled: seedingEnabled && currentUserId != null,
        staleTime: 0,
    })

    const seeds = data?.seeds || []
    const totals = data?.totals || { staked: 0, earned_back: 0 }
    if (!seedingEnabled || seeds.length === 0) return null
    const net = totals.earned_back - totals.staked

    return (
        <div className={styles.seedsSection}>
            <div className={styles.seedsHeader}>
                <div className={styles.seedsTitle}>
                    <Sprout size={15} aria-hidden />
                    <span>Seeds</span>
                </div>
                <div className={styles.seedsTotals}>
                    <span>{formatSatsAmount(totals.staked, { compactSats: false })} in</span>
                    <span className={styles.seedsDot}>·</span>
                    <span
                        className={cn(
                            styles.seedsBack,
                            net >= 0 ? styles.seedsNetPositive : undefined,
                        )}
                    >
                        {formatSatsAmount(totals.earned_back, { compactSats: false })} back
                    </span>
                </div>
            </div>

            <div className={styles.seedsList}>
                {seeds.map(seed => (
                    <div key={seed.answer_id} className={styles.seedRow}>
                        <div className={styles.seedRowMain}>
                            <div className={styles.seedRowTop}>
                                {seed.position != null && (
                                    <span className={styles.seedPositionChip}>
                                        seed #{seed.position}
                                    </span>
                                )}
                                {seed.author_name && (
                                    <span className={styles.seedAuthor}>@{seed.author_name}</span>
                                )}
                                {seed.pending && (
                                    <span className={styles.seedPending}>pending</span>
                                )}
                            </div>
                            {seed.text_preview && (
                                <div className={styles.seedPreview}>{seed.text_preview}</div>
                            )}
                        </div>
                        <div className={styles.seedRowAmounts}>
                            <div className={styles.seedIn}>
                                {formatSatsAmount(seed.my_sats, { compactSats: false })} in
                            </div>
                            <div
                                className={cn(
                                    styles.seedEarned,
                                    seed.earned_back_sats >= seed.my_sats &&
                                        styles.seedsNetPositive,
                                )}
                            >
                                {seed.earned_back_sats > 0
                                    ? `+${formatSatsAmount(seed.earned_back_sats, {
                                          compactSats: false,
                                      })} back`
                                    : 'no earn-back yet'}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
